import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { moveTargets, type CostTree } from '../lib/tree'

const TOP = '__top__'
/** Indentation of a target by its level (0 = top level, 1–3 = group level). */
const TARGET_INDENT = ['', '', 'pl-4', 'pl-8']

interface Props {
  nodeId: string | null
  tree: CostTree
  onClose: () => void
  onMove: (nodeId: string, parentId: string | null, index: number) => Promise<boolean>
}

const MoveNodeDialog: React.FC<Props> = ({ nodeId, tree, onClose, onMove }) => {
  const { t } = useTranslation()
  const targets = useMemo(() => (nodeId ? moveTargets(tree, nodeId) : []), [nodeId, tree])
  const [target, setTarget] = useState('')
  const [submitting, setSubmitting] = useState(false)
  useEffect(() => { setTarget('') }, [nodeId])
  const node = nodeId ? tree.byId.get(nodeId)?.activity : undefined

  const submit = async () => {
    if (!nodeId || !target) return
    const parentId = target === TOP ? null : target
    // At the end of the destination; the server clamps the index.
    const index = parentId === null ? tree.roots.length : tree.byId.get(parentId)?.children.length ?? 0
    setSubmitting(true)
    const ok = await onMove(nodeId, parentId, index)
    setSubmitting(false)
    if (ok) onClose()
  }

  return (
    <Dialog open={!!nodeId} onOpenChange={(open) => { if (!open && !submitting) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{t('CostControl_move_title', { name: node?.name ?? '' })}</DialogTitle></DialogHeader>
        {targets.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('CostControl_move_no_targets')}</p>
        ) : (
          <div className="space-y-2">
            <Label>{t('CostControl_move_target')}</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="h-10"><SelectValue placeholder={t('CostControl_move_pick')} /></SelectTrigger>
              <SelectContent className="max-h-80">
                {targets.map((tg) => (
                  <SelectItem key={tg.id ?? TOP} value={tg.id ?? TOP}>
                    <span className={TARGET_INDENT[tg.depth]}>{tg.id === null ? t('CostControl_move_top_level') : tg.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={submitting}>{t('Common_cancel')}</Button>
          <Button onClick={() => { void submit() }} disabled={!target || submitting}>
            {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('CostControl_move')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default MoveNodeDialog
